import type { Tenant } from '../../services/api'
import { getTenantPortalUrl } from '../../config/api'

interface TenantListProps {
  tenants: Tenant[]
  onEdit?: (tenant: Tenant) => void
  onDelete?: (tenantId: string) => void
  onManageUsers?: (tenant: Tenant) => void
  onManageServices?: (tenant: Tenant) => void
  isReadOnly?: boolean
}

export default function TenantList({ tenants, onEdit, onDelete, onManageUsers, onManageServices, isReadOnly = false }: TenantListProps) {
  if (tenants.length === 0) {
    return (
      <div className="bg-gray-800 rounded-xl p-12 text-center">
        <svg
          className="w-16 h-16 text-gray-600 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
        <h3 className="text-xl font-semibold text-white mb-2">No hay clientes</h3>
        <p className="text-gray-400">
          Agrega tu primer cliente para comenzar a gestionar su seguridad.
        </p>
      </div>
    )
  }

  const getStatusBadge = (status: Tenant['status']) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400',
      trial: 'bg-yellow-500/20 text-yellow-400',
      suspended: 'bg-red-500/20 text-red-400'
    }
    const labels = {
      active: 'Activo',
      trial: 'Trial',
      suspended: 'Suspendido'
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    )
  }

  const getPlanBadge = (plan: Tenant['plan']) => {
    const styles = {
      free: 'bg-gray-500/20 text-gray-400',
      standard: 'bg-blue-500/20 text-blue-400',
      enterprise: 'bg-purple-500/20 text-purple-400'
    }
    const labels = {
      free: 'Free',
      standard: 'Standard',
      enterprise: 'Enterprise'
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[plan]}`}>
        {labels[plan]}
      </span>
    )
  }

  const getDashboardUrl = (slug: string) => {
    return getTenantPortalUrl(slug)
  }

  return (
    <div className="bg-gray-800 rounded-xl overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Cliente
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Estado
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Plan
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Integraciones
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Creado
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {tenants.map((tenant) => (
            <tr key={tenant.id} className="hover:bg-gray-700/50 transition-colors">
              <td className="px-6 py-4">
                <div className="flex items-center">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold mr-3"
                    style={{ backgroundColor: tenant.primary_color }}
                  >
                    {tenant.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-white font-medium">{tenant.name}</div>
                    <div className="text-gray-400 text-sm">{tenant.slug}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                {getStatusBadge(tenant.status)}
              </td>
              <td className="px-6 py-4">
                {getPlanBadge(tenant.plan)}
              </td>
              <td className="px-6 py-4">
                <div className="flex gap-2">
                  {tenant.crowdstrike_client_id && (
                    <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-xs">
                      CrowdStrike
                    </span>
                  )}
                  {tenant.jumpcloud_org_id && (
                    <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs">
                      JumpCloud
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 text-gray-400 text-sm">
                {new Date(tenant.created_at).toLocaleDateString('es-CL')}
              </td>
              <td className="px-6 py-4 text-right">
                <div className="flex items-center justify-end gap-2">
                  {/* View Dashboard - Always visible */}
                  <a
                    href={getDashboardUrl(tenant.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                    title="Ver Dashboard"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  {/* Manage Users - Hidden in read-only mode */}
                  {!isReadOnly && onManageUsers && (
                    <button
                      onClick={() => onManageUsers(tenant)}
                      className="text-gray-400 hover:text-blue-400 p-2 rounded-lg hover:bg-gray-700 transition-colors"
                      title="Gestionar Usuarios"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </button>
                  )}
                  {/* Manage Services - Hidden in read-only mode */}
                  {!isReadOnly && onManageServices && (
                    <button
                      onClick={() => onManageServices(tenant)}
                      className="text-gray-400 hover:text-green-400 p-2 rounded-lg hover:bg-gray-700 transition-colors"
                      title="Gestionar Servicios"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </button>
                  )}
                  {/* Edit - Hidden in read-only mode */}
                  {!isReadOnly && onEdit && (
                    <button
                      onClick={() => onEdit(tenant)}
                      className="text-gray-400 hover:text-jarix-orange p-2 rounded-lg hover:bg-gray-700 transition-colors"
                      title="Editar"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                  {/* Delete - Hidden in read-only mode */}
                  {!isReadOnly && onDelete && (
                    <button
                      onClick={() => onDelete(tenant.id)}
                      className="text-gray-400 hover:text-red-400 p-2 rounded-lg hover:bg-gray-700 transition-colors"
                      title="Eliminar"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
